import { useCallback, useEffect, useState } from "react";
import type { ChangeEvent, ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Hinweis, Auftrag } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen } from "@kundeninfo/ui";
import { laufzeitVorschauWerte } from "../components/PositionsEditor.js";
import { formatDatum } from "../format.js";
import "./KundeWichtiges.css";

/** Nur die Klientenmethoden, die dieser Reiter wirklich braucht — so lässt er sich im Test mit
 *  einer Handvoll vi.fn zeichnen (Muster aus KundeNotizen/KundeAuftraege). `listSalesOrders`
 *  füllt die optionale „an einen Auftrag"-Auswahl beim Anlegen. */
export type WichtigesClient = Pick<ApiClient,
  "listImportantNotes" | "createImportantNote" | "updateImportantNote" | "deleteImportantNote"
  | "listOrderRuntimeWarnings" | "listSalesOrders">;

/** Die Verdrahtung, die von App über KundenAkte hierher durchgereicht wird (erst in T9). */
export type WichtigesUmfeld = { client: WichtigesClient };

export type KundeWichtigesProps = WichtigesUmfeld & { kundeId: string };

type Gewicht = Hinweis["weight"];

/** Deutsche Beschriftung eines Gewichts. */
const GEWICHT_LABEL: Record<Gewicht, string> = {
  CRITICAL: "Kritisch",
  HIGH: "Hoch",
  STANDARD: "Standard",
};

/** Die auswählbaren Gewichte, dringlichstes zuerst (wie die Liste sortiert). */
const GEWICHTE: Gewicht[] = ["CRITICAL", "HIGH", "STANDARD"];

/** Eine ApiError-Meldung herausziehen, sonst ein verständlicher Standardtext. */
function alsMeldung(fehler: unknown, standard: string): string {
  if (fehler instanceof ApiError && fehler.message.trim() !== "") return fehler.message;
  return standard;
}

function oderNull(wert: string): string | null {
  return wert.trim() === "" ? null : wert;
}

/** Die Formularwerte. `salesOrderId` = "" heisst „am Kunden", sonst der gewählte Auftrag. */
type Entwurf = {
  text: string;
  weight: Gewicht;
  validUntil: string;
  salesOrderId: string;
};

function leererEntwurf(): Entwurf {
  return { text: "", weight: "STANDARD", validUntil: "", salesOrderId: "" };
}

/** Flacher Vergleich zweier Entwürfe — für die Rückfrage vor dem Abbrechen. */
function entwurfGleich(a: Entwurf, b: Entwurf): boolean {
  return a.text === b.text && a.weight === b.weight
    && a.validUntil === b.validUntil && a.salesOrderId === b.salesOrderId;
}

function entwurfAus(h: Hinweis): Entwurf {
  return {
    text: h.text,
    weight: h.weight,
    validUntil: h.validUntil ?? "",
    // Der Bezug ist beim Ändern fest (der Server lässt ihn nicht wechseln) — nur zur Anzeige.
    salesOrderId: h.salesOrderId ?? "",
  };
}

/**
 * Der Wichtiges-Reiter zeigt zweierlei:
 *
 *  1. die ECHTEN Hinweise (`importantNote`) — am Kunden ODER an einem seiner Aufträge, nach
 *     Gewicht (Kritisch → Hoch → Standard) sortiert; abgelaufene stehen verblasst in einem
 *     eigenen Abschnitt. Anlegen (Text/Gewicht/gültig bis, optional an einen Auftrag),
 *     Bearbeiten (mit `version`) und Löschen (Rückfrage).
 *  2. die ABGELEITETE Laufzeit-Ende-Warnung — die `listOrderRuntimeWarnings`-Aufträge als
 *     NICHT-editierbare Warn-Einträge („Auftrag … endet am …"). Kein gespeicherter Satz.
 *
 * Anlegen und Bearbeiten laufen im strikten Modalpanel (Hausregel, Entscheid Dirk vom
 * 06.09.2026) — dieselbe Maske wie vorher, nur im gemeinsamen Rahmen mit seinen zwei
 * Ausgängen und der Rückfrage vor dem Abbrechen. Vorher stand sie offen zwischen Kopf und
 * Liste: Ein Klick auf „Bearbeiten" an einem anderen Hinweis ersetzte den Entwurf
 * lautlos.
 *
 * Eigene Wurzelklasse `.kwic` (NICHT `.ka`/`.kauf` teilen — Kollisions-Lehre 01.09.,
 * Ruling P3A-A). Farben ausschliesslich aus den Design-Tokens (--ki-*, Marken-Grün zentral).
 */
export function KundeWichtiges(p: KundeWichtigesProps): ReactElement {
  const { client, kundeId } = p;

  const [hinweise, setHinweise] = useState<Hinweis[] | null>(null);
  const [warnungen, setWarnungen] = useState<Auftrag[]>([]);
  const [auftraege, setAuftraege] = useState<Auftrag[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  const [neuOffen, setNeuOffen] = useState(false);
  /** Der gerade bearbeitete Hinweis — Quelle der `version` beim Speichern. */
  const [bearbeitet, setBearbeitet] = useState<Hinweis | null>(null);
  const [entwurf, setEntwurf] = useState<Entwurf>(leererEntwurf);
  /**
   * Der Stand des Entwurfs beim ÖFFNEN — allein für die Rückfrage vor dem Abbrechen
   * (Hausregel „striktes Modalpanel", 06.09.2026).
   *
   * Er wird mitgeführt und nicht aus dem Hinweis nachgebildet, weil beide Masken denselben
   * Entwurf benutzen: Beim Anlegen gibt es gar keinen Hinweis, gegen den sich vergleichen
   * liesse, und beim Bearbeiten wäre `entwurfAus(bearbeitet)` zwar richtig — aber zwei
   * Vergleichsarten nebeneinander laufen mit dem nächsten Feld auseinander.
   */
  const [ausgang, setAusgang] = useState<Entwurf>(leererEntwurf);
  const [feldFehler, setFeldFehler] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);

  const laden = useCallback(async (): Promise<void> => {
    try {
      const { items } = await client.listImportantNotes(kundeId);
      setHinweise(items);
      setFehler(null);
    } catch (e) {
      setFehler(alsMeldung(e, "Die Hinweise konnten nicht geladen werden."));
    }
  }, [client, kundeId]);

  useEffect(() => { void laden(); }, [laden]);

  // Die abgeleiteten Laufzeit-Warnungen. Scheitert das, bleibt der Reiter bedienbar — nur die
  // Warnungen entfallen still (die echten Hinweise sind der Kern).
  useEffect(() => {
    let ab = false;
    client.listOrderRuntimeWarnings(kundeId)
      .then(({ items }) => { if (!ab) setWarnungen(items); })
      .catch(() => { /* abgeleitete Beigabe — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client, kundeId]);

  // Die Aufträge des Kunden für die optionale „an einen Auftrag"-Auswahl beim Anlegen.
  useEffect(() => {
    let ab = false;
    client.listSalesOrders(kundeId)
      .then(({ items }) => { if (!ab) setAuftraege(items); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client, kundeId]);

  const formularOffen = neuOffen || bearbeitet !== null;

  function schliessen(): void {
    setNeuOffen(false);
    setBearbeitet(null);
    setEntwurf(leererEntwurf());
    setAusgang(leererEntwurf());
    setFeldFehler(null);
  }

  /**
   * Der EINE Ausgang der Maske neben dem Speichern (Hausregel „striktes Modalpanel",
   * 06.09.2026): kein Ausgang unter laufendem Schreiben, Rückfrage, sobald etwas
   * Ungespeichertes darin steht. Knopf und Escape gehen durch diese Funktion.
   */
  function abbrechen(): void {
    if (speichert) return;
    if (!darfAbbrechen(!entwurfGleich(entwurf, ausgang))) return;
    schliessen();
  }

  function neuOeffnen(): void {
    setEntwurf(leererEntwurf());
    setAusgang(leererEntwurf());
    setFeldFehler(null);
    setMeldung(null);
    setBearbeitet(null);
    setNeuOffen(true);
  }

  function bearbeitenOeffnen(h: Hinweis): void {
    setEntwurf(entwurfAus(h));
    setAusgang(entwurfAus(h));
    setFeldFehler(null);
    setMeldung(null);
    setNeuOffen(false);
    setBearbeitet(h);
  }

  async function anlegen(): Promise<void> {
    if (entwurf.text.trim() === "") { setFeldFehler("Bitte einen Text angeben."); return; }
    setSpeichert(true);
    setFeldFehler(null);
    try {
      // Ohne gewählten Auftrag ein Kundenhinweis; mit einem ein Auftragshinweis.
      const bezug = entwurf.salesOrderId === "" ? {} : { salesOrderId: entwurf.salesOrderId };
      await client.createImportantNote(kundeId, {
        ...bezug, text: entwurf.text, weight: entwurf.weight,
        validUntil: oderNull(entwurf.validUntil),
      });
      schliessen();
      await laden();
    } catch (e) {
      setFeldFehler(alsMeldung(e, "Der Hinweis konnte nicht gespeichert werden."));
    } finally {
      setSpeichert(false);
    }
  }

  async function aendernSpeichern(): Promise<void> {
    if (bearbeitet === null) return;
    if (entwurf.text.trim() === "") { setFeldFehler("Bitte einen Text angeben."); return; }
    setSpeichert(true);
    setFeldFehler(null);
    try {
      // version = die gelesene Version: Ist der Hinweis zwischenzeitlich fremd geändert worden,
      // antwortet die API mit 409 statt still zu überschreiben.
      await client.updateImportantNote(kundeId, bearbeitet.id, bearbeitet.version, {
        text: entwurf.text, weight: entwurf.weight, validUntil: oderNull(entwurf.validUntil),
      });
      schliessen();
      await laden();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setMeldung("Dieser Hinweis wurde zwischenzeitlich geändert. Der aktuelle Stand ist "
          + "neu geladen — bitte erneut bearbeiten.");
        schliessen();
        await laden();
      } else {
        setFeldFehler(alsMeldung(e, "Die Änderung konnte nicht gespeichert werden."));
      }
    } finally {
      setSpeichert(false);
    }
  }

  async function loeschen(h: Hinweis): Promise<void> {
    if (!window.confirm("Diesen Hinweis wirklich löschen?")) return;
    try {
      await client.deleteImportantNote(kundeId, h.id);
      setMeldung(null);
      await laden();
    } catch (e) {
      setMeldung(alsMeldung(e, "Der Hinweis konnte nicht gelöscht werden."));
    }
  }

  /**
   * WARUM dieser Auftrag in der Warnliste steht — in Worten.
   *
   * Der Server warnt seit Paket 3b-1b aus ZWEI Gründen (salesOrder.ts, `warndatum`): weil
   * ein befristeter Auftrag ausläuft, ODER weil der Kündigungsstichtag zum Ende des
   * Kalenderjahres naht — und wo es beide gibt, zum FRÜHEREN der beiden Termine. Welcher es
   * war, sagt die Antwort nicht; sie führt das massgebliche Datum gar nicht.
   *
   * Deshalb nennt diese Zeile, was sie belegen kann, und rechnet NICHTS nach: das Enddatum,
   * wenn es eines gibt, und die vereinbarte Frist, wenn sie zum Jahresende wirkt. Ein
   * nachgerechneter Stichtag wäre eine zweite Wahrheit über eine Regel, die in der Domäne
   * steht — und `web` darf sie nicht einmal importieren. Vorher stand hier bei einem
   * unbefristeten Auftrag „endet am —": Ein unbefristeter Auftrag endet nie, und ein
   * Gedankenstrich ist kein Grund (Fund G6 aus Aufgabe 2).
   *
   * Die Frist-Worte kommen aus `laufzeitVorschauWerte` — derselben Nachbildung, die auch
   * die Textvorschau am Angebot benutzt („3 Monate zum Ende des Kalenderjahres").
   */
  function warngruende(a: Auftrag): string[] {
    const gruende: string[] = [];
    if (!a.openEnded && a.endDate !== null) {
      gruende.push(`endet am ${formatDatum(a.endDate)}`);
    }
    if (a.noticeReference === "CALENDAR_YEAR_END") {
      // Seit Migration 031 nimmt `laufzeitVorschauWerte` ROHTEXTE entgegen (das
      // Formular-Format), keine vorkonvertierten Zahlen mehr — die drei Paare, um die es
      // hier nicht geht, gehen deshalb als LEERE Zeichenkette mit, nicht mehr als `null`.
      //
      // Fund M6 der Abschlussprüfung: Die Einheit wird NICHT erfunden, wenn sie fehlt.
      // `noticePeriodUnit` war schon vor 031 nullbar, und ein Auftrag aus dem Bestand kann
      // eine Kündigungsfrist als Zahl tragen, ohne dass ihre Einheit gepflegt ist. Ein
      // `?? "MONTHS"` machte aus „3" damals kommentarlos „3 Monate" — eine Vereinbarung,
      // die niemand eingetragen hat. Fehlt die Einheit, bleibt es beim blossen Grund ohne
      // Zahl, genau wie bei einer halben Frist (Bezug ohne Wert).
      const frist = a.noticePeriodUnit === null ? undefined : laufzeitVorschauWerte({
        termValue: "", termUnit: "MONTHS",
        minimumTermValue: "", minimumTermUnit: "MONTHS",
        renewalPeriodValue: "", renewalPeriodUnit: "MONTHS",
        noticePeriodValue: a.noticePeriodValue === null ? "" : String(a.noticePeriodValue),
        noticePeriodUnit: a.noticePeriodUnit,
        noticeReference: a.noticeReference,
      })["laufzeit.kuendigungsfrist"];
      gruende.push(frist === undefined
        // Eine halbe Frist (Bezug ohne Wert ODER Wert ohne Einheit) ergibt keinen Satz —
        // dann bleibt der blosse Grund stehen, statt einer Lücke oder einer Erfindung.
        ? "Kündigungsfrist läuft"
        : `Kündigungsfrist läuft: ${frist}`);
    }
    // Kann der Server einmal aus einem Grund warnen, den die Zeile nicht hergibt, steht
    // wenigstens da, worum es geht — nie eine leere Zeile.
    return gruende.length > 0 ? gruende : ["Laufzeit-Ende steht bevor"];
  }

  /** Das Ziel eines Hinweises in Worten — „Am Kunden" oder „Auftrag …". */
  function zielText(h: Hinweis): string {
    if (h.salesOrderId === null) return "Am Kunden";
    const nummer = h.orderNumber ?? "";
    const titel = h.orderTitle ?? "";
    return `Auftrag ${nummer}${titel === "" ? "" : ` · ${titel}`}`.trim();
  }

  const gueltige = (hinweise ?? []).filter((h) => !h.expired);
  const abgelaufene = (hinweise ?? []).filter((h) => h.expired);

  function hinweisEintrag(h: Hinweis, abgelaufen: boolean): ReactElement {
    return (
      <li key={h.id}
          className={`kwic__eintrag${abgelaufen ? " kwic__eintrag--abgelaufen" : ""}`}>
        <div className="kwic__zeile">
          <span className={`kwic__gewicht kwic__gewicht--${h.weight.toLowerCase()}`}>
            {GEWICHT_LABEL[h.weight]}
          </span>
          <span className="kwic__ziel">{zielText(h)}</span>
          {h.validUntil !== null && (
            <span className="kwic__gueltig">
              {abgelaufen ? "abgelaufen am " : "gültig bis "}{formatDatum(h.validUntil)}
            </span>
          )}
        </div>
        <div className="kwic__text">{h.text}</div>
        {/* Gesperrt, solange das Panel steht (Fixrunde 1, 07.09.2026): Diese Knöpfe liegen
            hinter dem Schleier und wären sonst Knöpfe, die niemand drücken kann — und in
            jsdom, wo nichts den Klick abfängt, ein grüner Test auf eine klemmende Maske. */}
        <div className="kwic__aktionen">
          <button type="button" className="kwic__aktion" disabled={formularOffen}
                  onClick={() => bearbeitenOeffnen(h)}>
            Bearbeiten
          </button>
          <button type="button" className="kwic__aktion kwic__aktion--gefahr"
                  disabled={formularOffen}
                  onClick={() => void loeschen(h)}>
            Löschen
          </button>
        </div>
      </li>
    );
  }

  return (
    <div className="kwic">
      <div className="kwic__kopf">
        <h3 className="kwic__titel">Wichtiges</h3>
        {!formularOffen && (
          <button type="button" className="kwic__primaer" onClick={neuOeffnen}>
            Neuer Hinweis
          </button>
        )}
      </div>

      {fehler !== null && <p className="kwic__meldung" role="alert">{fehler}</p>}
      {meldung !== null && <p className="kwic__meldung" role="status">{meldung}</p>}

      {formularOffen && (
        <Modal titel={bearbeitet === null ? "Neuer Hinweis" : "Hinweis bearbeiten"}
          breite="mittel" onAbbrechen={abbrechen} abbrechenGesperrt={speichert}>
          <div className="kwic__feld">
            <label className="kwic__label" htmlFor="kwic-text">Text</label>
            <textarea
              id="kwic-text"
              className="kwic__eingabe"
              value={entwurf.text}
              rows={3}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setEntwurf({ ...entwurf, text: e.target.value })}
            />
          </div>
          <div className="kwic__felder">
            <div className="kwic__feld">
              <label className="kwic__label" htmlFor="kwic-gewicht">Gewicht</label>
              <select
                id="kwic-gewicht"
                className="kwic__select"
                value={entwurf.weight}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  setEntwurf({ ...entwurf, weight: e.target.value as Gewicht })}
              >
                {GEWICHTE.map((g) => (
                  <option key={g} value={g}>{GEWICHT_LABEL[g]}</option>
                ))}
              </select>
            </div>
            <div className="kwic__feld">
              <label className="kwic__label" htmlFor="kwic-gueltig">Gültig bis</label>
              <input
                id="kwic-gueltig"
                type="date"
                className="kwic__eingabe"
                value={entwurf.validUntil}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setEntwurf({ ...entwurf, validUntil: e.target.value })}
              />
            </div>
            {/* Der Bezug ist nur beim Anlegen wählbar — beim Ändern lässt der Server ihn nicht
                wechseln. */}
            {bearbeitet === null && (
              <div className="kwic__feld">
                <label className="kwic__label" htmlFor="kwic-bezug">Bezug</label>
                <select
                  id="kwic-bezug"
                  className="kwic__select"
                  value={entwurf.salesOrderId}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setEntwurf({ ...entwurf, salesOrderId: e.target.value })}
                >
                  <option value="">Am Kunden</option>
                  {auftraege.map((a) => (
                    <option key={a.id} value={a.id}>
                      Auftrag {a.orderNumber} · {a.title}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {feldFehler !== null && <p className="kwic__feldfehler" role="alert">{feldFehler}</p>}

          <ModalFuss speichernText="Speichern" speichernGesperrt={speichert}
            abbrechenGesperrt={speichert}
            onSpeichern={() => {
              void (bearbeitet === null ? anlegen() : aendernSpeichern());
            }}
            onAbbrechen={abbrechen} />
        </Modal>
      )}

      {warnungen.length > 0 && (
        <section className="kwic__warnungen" aria-label="Laufzeit-Warnungen">
          <h4 className="kwic__abschnitt">Laufzeit-Ende</h4>
          <ul className="kwic__liste">
            {warnungen.map((a) => (
              <li key={a.id} className="kwic__warnung">
                <span className="kwic__warn-symbol" aria-hidden="true">⚠</span>
                <span className="kwic__warn-text">
                  Auftrag {a.orderNumber} · {a.title} — {warngruende(a).join(" · ")}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {hinweise !== null && gueltige.length === 0 && abgelaufene.length === 0 && (
        <p className="kwic__leer">Noch keine Hinweise zu diesem Kunden.</p>
      )}

      {gueltige.length > 0 && (
        <ul className="kwic__liste">
          {gueltige.map((h) => hinweisEintrag(h, false))}
        </ul>
      )}

      {abgelaufene.length > 0 && (
        <section className="kwic__abgelaufen-bereich" aria-label="Abgelaufene Hinweise">
          <h4 className="kwic__abschnitt">Abgelaufen</h4>
          <ul className="kwic__liste">
            {abgelaufene.map((h) => hinweisEintrag(h, true))}
          </ul>
        </section>
      )}
    </div>
  );
}
